import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../LangContext";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const COPY = {
  sv: {
    pick_date: "Välj datum",
    pick_slot: "Välj tid",
    no_slots: "Inga lediga tider denna dag",
    loading: "Hämtar lediga tider…",
    not_connected:
      "Direktbokning är tillfälligt otillgänglig. Använd kontaktformuläret nedan eller ring oss direkt.",
    months: [
      "januari","februari","mars","april","maj","juni",
      "juli","augusti","september","oktober","november","december",
    ],
    weekdays: ["Mån","Tis","Ons","Tor","Fre","Lör","Sön"],
    prev: "Föregående",
    next: "Nästa",
    selected: "Vald tid",
    none: "Ingen vald",
  },
  en: {
    pick_date: "Pick a date",
    pick_slot: "Pick a time",
    no_slots: "No available times on this date",
    loading: "Loading available times…",
    not_connected:
      "Direct booking is temporarily unavailable. Please use the contact form below or call us.",
    months: [
      "January","February","March","April","May","June",
      "July","August","September","October","November","December",
    ],
    weekdays: ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"],
    prev: "Previous",
    next: "Next",
    selected: "Selected slot",
    none: "None selected",
  },
};

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function sameYmd(a, b) {
  return ymd(a) === ymd(b);
}

export default function CalendarBooking({ onSlotChange, selectedSlot }) {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.sv;

  const [window, setWindow] = useState(null);
  const [availableDates, setAvailableDates] = useState(null);
  const [unavailableReason, setUnavailableReason] = useState(null);
  const [viewMonth, setViewMonth] = useState(startOfMonth(new Date()));
  const [pickedDate, setPickedDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Load window + available dates on mount
  useEffect(() => {
    (async () => {
      try {
        const w = await axios.get(`${API}/calendar/window`);
        setWindow(w.data);
        // Set view to the earliest bookable month
        const earliest = new Date(w.data.earliest + "T00:00:00");
        setViewMonth(startOfMonth(earliest));
      } catch (_) {}
      try {
        const d = await axios.get(`${API}/calendar/available-dates`);
        setAvailableDates(new Set(d.data.dates));
      } catch (e) {
        const detail = e?.response?.data?.detail || "";
        if (e?.response?.status === 503) setUnavailableReason("not_connected");
        else setUnavailableReason(detail || "error");
        setAvailableDates(new Set());
      }
    })();
  }, []);

  const earliestDate = window ? new Date(window.earliest + "T00:00:00") : null;
  const latestDate = window ? new Date(window.latest + "T00:00:00") : null;

  const monthGrid = useMemo(() => {
    const first = startOfMonth(viewMonth);
    const startWeekday = (first.getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewMonth]);

  const canPrev = useMemo(() => {
    if (!earliestDate) return false;
    const prev = addMonths(viewMonth, -1);
    return prev >= startOfMonth(earliestDate);
  }, [viewMonth, earliestDate]);

  const canNext = useMemo(() => {
    if (!latestDate) return true;
    const next = addMonths(viewMonth, 1);
    return next <= startOfMonth(latestDate);
  }, [viewMonth, latestDate]);

  const isSelectable = (d) => {
    if (!d || !earliestDate || !latestDate) return false;
    if (d < earliestDate) return false;
    if (d > latestDate) return false;
    if (d.getDay() === 0 || d.getDay() === 6) return false;
    if (availableDates && !availableDates.has(ymd(d))) return false;
    return true;
  };

  const onPickDate = async (d) => {
    setPickedDate(d);
    setSlots(null);
    onSlotChange?.(null);
    setLoadingSlots(true);
    try {
      const r = await axios.get(`${API}/calendar/slots?date=${ymd(d)}`);
      setSlots(r.data.slots || []);
    } catch (_) {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const onPickSlot = (slot) => {
    onSlotChange?.({ ...slot, date: pickedDate ? ymd(pickedDate) : null });
  };

  if (unavailableReason === "not_connected") {
    return (
      <div
        className="border border-line bg-stone50 p-6 rounded-sm"
        data-testid="calendar-unavailable"
      >
        <p className="text-sm text-ink2 leading-relaxed">{c.not_connected}</p>
      </div>
    );
  }

  return (
    <div data-testid="calendar-booking">
      <div className="flex items-baseline justify-between mb-4">
        <p className="eyebrow text-ink2">{c.pick_date}</p>
        <div className="flex items-center gap-3 text-xs">
          <button
            type="button"
            onClick={() => canPrev && setViewMonth((m) => addMonths(m, -1))}
            disabled={!canPrev}
            data-testid="cal-prev-month"
            className="text-ink2 hover:text-ink disabled:opacity-30 transition-colors"
          >
            ←
          </button>
          <span className="font-serif text-lg text-ink min-w-[140px] text-center">
            {c.months[viewMonth.getMonth()]} {viewMonth.getFullYear()}
          </span>
          <button
            type="button"
            onClick={() => canNext && setViewMonth((m) => addMonths(m, 1))}
            disabled={!canNext}
            data-testid="cal-next-month"
            className="text-ink2 hover:text-ink disabled:opacity-30 transition-colors"
          >
            →
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2 text-[0.65rem] uppercase tracking-widest text-ink2">
        {c.weekdays.map((w) => (
          <div key={w} className="text-center py-1">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
        {monthGrid.map((d, i) => {
          if (!d) return <div key={i} />;
          const sel = isSelectable(d);
          const isPicked = pickedDate && sameYmd(d, pickedDate);
          return (
            <button
              key={i}
              type="button"
              disabled={!sel}
              onClick={() => sel && onPickDate(d)}
              data-testid={`cal-day-${ymd(d)}`}
              className={`aspect-square text-sm font-sans transition-all duration-200 rounded-sm
                ${
                  isPicked
                    ? "bg-ink text-cream"
                    : sel
                    ? "text-ink hover:bg-stone50 border border-transparent hover:border-line"
                    : "text-ink2/30 cursor-not-allowed"
                }`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {pickedDate && (
          <motion.div
            key={ymd(pickedDate)}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="mt-8"
          >
            <p className="eyebrow text-ink2 mb-3">
              {c.pick_slot} · {pickedDate.toLocaleDateString(lang === "en" ? "en-GB" : "sv-SE", { weekday: "long", day: "numeric", month: "long" })}
            </p>
            {loadingSlots ? (
              <p className="text-sm text-ink2 italic">{c.loading}</p>
            ) : slots && slots.length === 0 ? (
              <p className="text-sm text-ink2 italic" data-testid="cal-no-slots">{c.no_slots}</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="cal-slots">
                {(slots || []).map((s) => {
                  const picked = selectedSlot && selectedSlot.start === s.start;
                  return (
                    <button
                      key={s.start}
                      type="button"
                      onClick={() => onPickSlot(s)}
                      data-testid={`cal-slot-${s.label}`}
                      className={`text-sm py-2.5 rounded-sm border transition-all duration-200
                        ${
                          picked
                            ? "bg-ink text-cream border-ink"
                            : "border-line text-ink hover:border-ink hover:bg-stone50"
                        }`}
                    >
                      {s.label.split(" – ")[0]}
                    </button>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
